/**
 * AssistantTab — Chat + voice container for Charlotte AI assistant.
 *
 * Modes:
 *   - Text: TranscriptList + TextInput
 *   - Voice: TranscriptList + VoiceButton (prominently centered)
 *
 * Degrades gracefully when:
 *   - AI endpoint not configured
 *   - Browser doesn't support MediaStream (voice disabled, text still works)
 */
import { createElement, useState, useCallback, useMemo } from 'react';
import { useWidgetConfig } from '../../context';
import { useCharlotteChat } from '../../hooks/useCharlotteChat';
import { useCharlotteVoice, type VoiceTranscript } from '../../hooks/useCharlotteVoice';
import { TranscriptList } from './TranscriptList';
import { TextInput } from './TextInput';
import { VoiceButton, type VoiceState } from './VoiceButton';
import type { ChatTranscript } from '../../hooks/useCharlotteChat';

type Mode = 'text' | 'voice';

export function AssistantTab() {
  const config = useWidgetConfig();
  const charlotte = config.charlotte;

  const [mode, setMode] = useState<Mode>('text');

  // Not configured guard
  if (!charlotte?.endpoint) {
    return createElement(
      'div',
      {
        style: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '24px',
          color: 'var(--sw-text-secondary)',
          fontSize: '14px',
          textAlign: 'center' as const,
        },
      },
      'AI assistant is not configured for this product.',
    );
  }

  return createElement(AssistantTabInner, { charlotte, mode, setMode });
}

// Inner component to avoid hook call after early return
interface AssistantTabInnerProps {
  charlotte: NonNullable<import('../../types').CharlotteConfig>;
  mode: Mode;
  setMode: (mode: Mode) => void;
}

function AssistantTabInner({ charlotte, mode, setMode }: AssistantTabInnerProps) {
  // Text chat hook
  const chat = useCharlotteChat({
    endpoint: charlotte.endpoint,
    systemPrompt: charlotte.systemPrompt,
    auth: charlotte.auth,
    autoConnect: true,
  });

  // Voice hook — only used when voice mode is active
  const voice = useCharlotteVoice({
    apiUrl: charlotte.endpoint.replace(/^ws/, 'http'),
    instructions: charlotte.systemPrompt,
    onTranscript: useCallback(
      (_t: VoiceTranscript) => {
        // Voice transcripts are already tracked in voice.transcripts
      },
      [],
    ),
  });

  // Merge transcripts from both sources, sorted by timestamp
  const allTranscripts = useMemo(() => {
    const merged: ChatTranscript[] = [
      ...chat.transcripts,
      ...voice.transcripts.map((vt) => ({
        id: vt.id,
        source: vt.source,
        text: vt.text,
        timestamp: vt.timestamp,
      })),
    ];
    merged.sort((a, b) => a.timestamp - b.timestamp);

    // Deduplicate by text+timestamp proximity
    const deduped: ChatTranscript[] = [];
    for (const t of merged) {
      const isDupe = deduped.some(
        (d) =>
          d.text === t.text &&
          d.source === t.source &&
          Math.abs(d.timestamp - t.timestamp) < 2000,
      );
      if (!isDupe) deduped.push(t);
    }
    return deduped;
  }, [chat.transcripts, voice.transcripts]);

  // Determine if we're waiting for a response
  const isThinking =
    allTranscripts.length > 0 &&
    allTranscripts[allTranscripts.length - 1].source === 'user' &&
    (chat.connected || voice.isActive);

  // Voice state mapping
  const voiceState: VoiceState = voice.isSpeaking
    ? 'processing'
    : voice.isActive
      ? 'listening'
      : 'idle';

  const handleVoiceToggle = useCallback(() => {
    if (voice.isActive) {
      voice.stop();
    } else {
      voice.start();
    }
  }, [voice]);

  const handleModeSwitch = useCallback(
    (newMode: Mode) => {
      if (newMode === 'text' && voice.isActive) {
        voice.stop();
      }
      setMode(newMode);
    },
    [voice, setMode],
  );

  return createElement(
    'div',
    {
      style: {
        display: 'flex',
        flexDirection: 'column' as const,
        height: '100%',
        padding: '12px',
        boxSizing: 'border-box' as const,
        overflow: 'hidden',
      },
    },
    // Mode toggle pills (only show if voice is supported)
    voice.isSupported
      ? createElement(
          'div',
          {
            style: {
              display: 'flex',
              gap: '4px',
              padding: '4px',
              backgroundColor: 'var(--sw-bg-surface)',
              borderRadius: 'var(--sw-border-radius)',
              marginBottom: '8px',
              flexShrink: 0,
            },
            role: 'tablist',
            'aria-label': 'Chat mode',
          },
          createElement(
            'button',
            {
              type: 'button',
              role: 'tab',
              'aria-selected': mode === 'text',
              onClick: () => handleModeSwitch('text'),
              style: {
                flex: 1,
                padding: '8px 12px',
                border: 'none',
                borderRadius: 'calc(var(--sw-border-radius) - 2px)',
                backgroundColor:
                  mode === 'text' ? 'var(--sw-accent-primary)' : 'transparent',
                color: mode === 'text' ? '#000' : 'var(--sw-text-secondary)',
                fontSize: '13px',
                fontWeight: 600,
                fontFamily: 'var(--sw-font-family)',
                cursor: 'pointer',
                minHeight: '36px',
              },
            },
            'Text',
          ),
          createElement(
            'button',
            {
              type: 'button',
              role: 'tab',
              'aria-selected': mode === 'voice',
              onClick: () => handleModeSwitch('voice'),
              style: {
                flex: 1,
                padding: '8px 12px',
                border: 'none',
                borderRadius: 'calc(var(--sw-border-radius) - 2px)',
                backgroundColor:
                  mode === 'voice' ? 'var(--sw-accent-primary)' : 'transparent',
                color: mode === 'voice' ? '#000' : 'var(--sw-text-secondary)',
                fontSize: '13px',
                fontWeight: 600,
                fontFamily: 'var(--sw-font-family)',
                cursor: 'pointer',
                minHeight: '36px',
              },
            },
            'Voice',
          ),
        )
      : null,
    // Connection status indicator
    !chat.connected && !voice.isActive
      ? createElement(
          'div',
          {
            style: {
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              marginBottom: '8px',
              borderRadius: 'var(--sw-border-radius)',
              backgroundColor: 'var(--sw-bg-surface)',
              fontSize: '12px',
              color: 'var(--sw-text-secondary)',
              flexShrink: 0,
            },
          },
          createElement('div', {
            style: {
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: chat.connecting ? '#f59e0b' : '#ef4444',
              flexShrink: 0,
            },
          }),
          chat.connecting ? 'Connecting...' : 'Disconnected',
          !chat.connecting
            ? createElement(
                'button',
                {
                  type: 'button',
                  onClick: chat.connect,
                  style: {
                    marginLeft: 'auto',
                    padding: '4px 8px',
                    border: '1px solid var(--sw-border)',
                    borderRadius: 'var(--sw-border-radius)',
                    backgroundColor: 'transparent',
                    color: 'var(--sw-accent-primary)',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontFamily: 'var(--sw-font-family)',
                    minHeight: '28px',
                  },
                },
                'Reconnect',
              )
            : null,
        )
      : null,
    // Transcript list
    createElement(TranscriptList, {
      transcripts: allTranscripts,
      thinking: isThinking,
    }),
    // Input area (depends on mode)
    mode === 'text'
      ? createElement(TextInput, {
          onSend: chat.send,
          disabled: !chat.connected,
          placeholder: charlotte.placeholder ?? 'Ask Charlotte...',
        })
      : createElement(
          'div',
          {
            style: {
              display: 'flex',
              justifyContent: 'center',
              padding: '16px 0 8px',
              borderTop: '1px solid var(--sw-border)',
              flexShrink: 0,
            },
          },
          createElement(VoiceButton, {
            state: voiceState,
            onClick: handleVoiceToggle,
          }),
        ),
  );
}
